import React, { useState } from 'react';
import { api, Variable, DataPoint, DataPointValue } from '../api/client';
import { formatShortDate } from '../utils/date';

interface PaginationProps {
  page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
}

interface Props {
  questionId: string;
  variables: Variable[];
  dataPoints: DataPoint[];
  pagination: PaginationProps;
  onPageChange: (page: number) => void | Promise<void>;
  onDataChanged: () => void;
}

const formatNow = (): string => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};

const DataPointManager: React.FC<Props> = ({
  questionId,
  variables,
  dataPoints,
  pagination,
  onPageChange,
  onDataChanged,
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [values, setValues] = useState<Record<string, any>>({});
  const [descriptions, setDescriptions] = useState<Record<string, string>>({});
  const [recordedAt, setRecordedAt] = useState<string>(formatNow());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [deletingDataPointId, setDeletingDataPointId] = useState<string | null>(null);
  const [editingDataPoint, setEditingDataPoint] = useState<DataPoint | null>(null);
  const [editValues, setEditValues] = useState<Record<string, any>>({});
  const [editDescriptions, setEditDescriptions] = useState<Record<string, string>>({});
  const [editRecordedAt, setEditRecordedAt] = useState<string>('');
  const [expandedDataPointId, setExpandedDataPointId] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate that all variables have values
    const missingVars = variables.filter(v => values[v.id] === undefined || values[v.id] === '');
    if (missingVars.length > 0) {
      setError(`Please provide values for all variables`);
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setSuccess(false);

      // Convert values to appropriate types and wrap in DataPointValue format
      const convertedValues: Record<string, DataPointValue> = {};
      for (const variable of variables) {
        const rawValue = values[variable.id];
        let value: number | string | boolean;
        if (variable.type === 'boolean') {
          value = rawValue === 'true' || rawValue === true;
        } else if (variable.type === 'numeric') {
          value = parseFloat(rawValue);
        } else {
          value = rawValue;
        }
        const dpValue: DataPointValue = { value };
        if (descriptions[variable.id]) {
          dpValue.description = descriptions[variable.id];
        }
        convertedValues[variable.id] = dpValue;
      }

      // Convert local datetime to ISO 8601 format
      const recordedAtISO = new Date(recordedAt).toISOString();

      await api.createDataPoint(questionId, convertedValues, recordedAtISO);
      setShowAddModal(false);
      setValues({});
      setDescriptions({});
      setRecordedAt(formatNow());
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      onDataChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save data point');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenAdd = () => {
    setValues({});
    setDescriptions({});
    setRecordedAt(formatNow());
    setError(null);
    setShowAddModal(true);
  };

  const handleCancelAdd = () => {
    setShowAddModal(false);
    setValues({});
    setDescriptions({});
    setError(null);
  };

  const handleEditClick = (dp: DataPoint) => {
    // Initialize edit values and descriptions from the datapoint
    const initialValues: Record<string, any> = {};
    const initialDescriptions: Record<string, string> = {};
    for (const variable of variables) {
      const dpValue = dp.values[variable.id];
      if (dpValue !== undefined) {
        if (variable.type === 'boolean') {
          initialValues[variable.id] = dpValue.value ? 'true' : 'false';
        } else {
          initialValues[variable.id] = dpValue.value;
        }
        if (dpValue.description) {
          initialDescriptions[variable.id] = dpValue.description;
        }
      }
    }
    setEditValues(initialValues);
    setEditDescriptions(initialDescriptions);

    // Initialize recorded_at from the datapoint's created_at
    const dpDate = new Date(dp.created_at);
    const formatted = new Date(dpDate.getTime() - dpDate.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
    setEditRecordedAt(formatted);

    setEditingDataPoint(dp);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingDataPoint) return;

    // Validate that all variables have values
    const missingVars = variables.filter(v => editValues[v.id] === undefined || editValues[v.id] === '');
    if (missingVars.length > 0) {
      setError(`Please provide values for all variables`);
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Convert values to appropriate types and wrap in DataPointValue format
      const convertedValues: Record<string, DataPointValue> = {};
      for (const variable of variables) {
        const rawValue = editValues[variable.id];
        let value: number | string | boolean;
        if (variable.type === 'boolean') {
          value = rawValue === 'true' || rawValue === true;
        } else if (variable.type === 'numeric') {
          value = parseFloat(rawValue);
        } else {
          value = rawValue;
        }
        const dpValue: DataPointValue = { value };
        if (editDescriptions[variable.id]) {
          dpValue.description = editDescriptions[variable.id];
        }
        convertedValues[variable.id] = dpValue;
      }

      // Convert local datetime to ISO 8601 format
      const recordedAtISO = new Date(editRecordedAt).toISOString();

      await api.updateDataPoint(editingDataPoint.id, {
        values: convertedValues,
        recorded_at: recordedAtISO,
      });
      setEditingDataPoint(null);
      setEditValues({});
      setEditDescriptions({});
      setEditRecordedAt('');
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      onDataChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update data point');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingDataPoint(null);
    setEditValues({});
    setEditDescriptions({});
    setEditRecordedAt('');
    setError(null);
  };

  const handleDeleteClick = async (dp: DataPoint) => {
    if (!window.confirm('Delete this data point? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingDataPointId(dp.id);
      setError(null);
      setSuccess(false);
      await api.deleteDataPoint(dp.id);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      onDataChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete data point');
      console.error(err);
    } finally {
      setDeletingDataPointId(null);
    }
  };

  const formatValue = (value: any, variable: Variable): string => {
    if (variable.type === 'boolean') {
      return value ? 'Yes' : 'No';
    } else if (variable.type === 'numeric') {
      return typeof value === 'number' ? value.toFixed(2) : value?.toString() || '-';
    }
    return value?.toString() || '-';
  };

  // Extract unique categorical values from existing datapoints for a given variable
  const getExistingCategoricalValues = (variableId: string): string[] => {
    const valuesSet = new Set<string>();
    for (const dp of dataPoints) {
      const dpValue = dp.values[variableId];
      if (dpValue !== undefined && typeof dpValue.value === 'string') {
        valuesSet.add(dpValue.value);
      }
    }
    return Array.from(valuesSet).sort();
  };

  const hasDescriptions = (dp: DataPoint): boolean => {
    return Object.values(dp.values).some(v => v.description);
  };

  return (
    <div className="data-point-manager">
      <div className="section-header">
        <h3>{pagination.total_count} datapoints</h3>
        <button className="btn btn-primary btn-sm" onClick={handleOpenAdd}>
          Add
        </button>
      </div>

      {success && <div className="success">Data point saved successfully!</div>}

      {/* Previously Logged Data Points */}
      {dataPoints.length > 0 ? (
        <>
          <div className="data-points-table">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  {variables.map((v) => (
                    <th key={v.id} title={v.description}>
                      {v.name}
                    </th>
                  ))}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {dataPoints.map((dp) => {
                  const expandable = hasDescriptions(dp);
                  return (
                    <React.Fragment key={dp.id}>
                      <tr
                        className={expandable ? 'clickable-row' : undefined}
                        onClick={expandable ? () => setExpandedDataPointId(
                          expandedDataPointId === dp.id ? null : dp.id
                        ) : undefined}
                      >
                        <td className="date-cell">
                          {formatShortDate(dp.created_at)}
                          <br />
                          <small>{new Date(dp.created_at).toLocaleTimeString()}</small>
                        </td>
                        {variables.map((v) => (
                          <td key={v.id}>
                            {dp.values && dp.values[v.id] !== undefined
                              ? formatValue(dp.values[v.id].value, v)
                              : '-'}
                          </td>
                        ))}
                        <td>
                          <div className="row-actions">
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={(e) => { e.stopPropagation(); handleEditClick(dp); }}
                              disabled={deletingDataPointId === dp.id}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={(e) => { e.stopPropagation(); void handleDeleteClick(dp); }}
                              disabled={deletingDataPointId === dp.id}
                            >
                              {deletingDataPointId === dp.id ? 'Deleting...' : 'Delete'}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expandedDataPointId === dp.id && (
                        <tr className="description-row">
                          <td colSpan={variables.length + 2}>
                            <div className="description-detail">
                              {variables.map((v) => {
                                const desc = dp.values[v.id]?.description;
                                if (!desc) return null;
                                return (
                                  <span key={v.id}>
                                    <strong>{v.name}:</strong> {desc}
                                  </span>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pagination.total_pages > 1 && (
            <div className="pagination-controls">
              <button
                className="btn btn-secondary btn-sm"
                disabled={pagination.page <= 1}
                onClick={() => onPageChange(pagination.page - 1)}
              >
                Previous
              </button>
              <span className="pagination-label">
                Page {pagination.page} of {pagination.total_pages}
              </span>
              <button
                className="btn btn-secondary btn-sm"
                disabled={pagination.page >= pagination.total_pages}
                onClick={() => onPageChange(pagination.page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      ) : (
        <p className="empty-state">No data points logged yet.</p>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={handleCancelAdd}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Data Point</h2>
              <button className="btn-close" onClick={handleCancelAdd}>&times;</button>
            </div>
            {error && <div className="error">{error}</div>}

            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="recorded-at">
                  When was this recorded?
                  <small>The date and time when this observation was made</small>
                </label>
                <input
                  id="recorded-at"
                  type="datetime-local"
                  value={recordedAt}
                  onChange={(e) => setRecordedAt(e.target.value)}
                  required
                />
              </div>

              {variables.map((variable) => (
                <div key={variable.id} className="form-group">
                  <label htmlFor={variable.id}>
                    {variable.name}
                    {variable.description && <small>{variable.description}</small>}
                  </label>

                  {variable.type === 'boolean' ? (
                    <select
                      id={variable.id}
                      value={values[variable.id] ?? ''}
                      onChange={(e) => setValues({ ...values, [variable.id]: e.target.value })}
                      required
                    >
                      <option value="">Select...</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  ) : variable.type === 'numeric' ? (
                    <input
                      id={variable.id}
                      type="number"
                      step="any"
                      value={values[variable.id] ?? ''}
                      onChange={(e) => setValues({ ...values, [variable.id]: e.target.value })}
                      placeholder="Enter a number"
                      required
                    />
                  ) : (
                    <>
                      <input
                        id={variable.id}
                        type="text"
                        list={`${variable.id}-options`}
                        value={values[variable.id] ?? ''}
                        onChange={(e) => setValues({ ...values, [variable.id]: e.target.value })}
                        placeholder="Select or enter a value"
                        required
                      />
                      <datalist id={`${variable.id}-options`}>
                        {getExistingCategoricalValues(variable.id).map((val) => (
                          <option key={val} value={val} />
                        ))}
                      </datalist>
                    </>
                  )}
                  <input
                    id={`${variable.id}-description`}
                    type="text"
                    value={descriptions[variable.id] ?? ''}
                    onChange={(e) => setDescriptions({ ...descriptions, [variable.id]: e.target.value })}
                    placeholder="Optional description (e.g., notes about this value)"
                    className="description-input"
                  />
                </div>
              ))}

              <div className="form-actions">
                <button
                  type="button"
                  onClick={handleCancelAdd}
                  className="btn btn-secondary"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save Data Point'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingDataPoint && (
        <div className="modal-overlay" onClick={handleCancelEdit}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Data Point</h2>
              <button className="btn-close" onClick={handleCancelEdit}>&times;</button>
            </div>
            {error && <div className="error">{error}</div>}

            <form onSubmit={handleEditSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="edit-recorded-at">
                  When was this recorded?
                  <small>The date and time when this observation was made</small>
                </label>
                <input
                  id="edit-recorded-at"
                  type="datetime-local"
                  value={editRecordedAt}
                  onChange={(e) => setEditRecordedAt(e.target.value)}
                  required
                />
              </div>

              {variables.map((variable) => (
                <div key={variable.id} className="form-group">
                  <label htmlFor={`edit-${variable.id}`}>
                    {variable.name}
                    {variable.description && <small>{variable.description}</small>}
                  </label>

                  {variable.type === 'boolean' ? (
                    <select
                      id={`edit-${variable.id}`}
                      value={editValues[variable.id] ?? ''}
                      onChange={(e) => setEditValues({ ...editValues, [variable.id]: e.target.value })}
                      required
                    >
                      <option value="">Select...</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  ) : variable.type === 'numeric' ? (
                    <input
                      id={`edit-${variable.id}`}
                      type="number"
                      step="any"
                      value={editValues[variable.id] ?? ''}
                      onChange={(e) => setEditValues({ ...editValues, [variable.id]: e.target.value })}
                      placeholder="Enter a number"
                      required
                    />
                  ) : (
                    <>
                      <input
                        id={`edit-${variable.id}`}
                        type="text"
                        list={`edit-${variable.id}-options`}
                        value={editValues[variable.id] ?? ''}
                        onChange={(e) => setEditValues({ ...editValues, [variable.id]: e.target.value })}
                        placeholder="Select or enter a value"
                        required
                      />
                      <datalist id={`edit-${variable.id}-options`}>
                        {getExistingCategoricalValues(variable.id).map((val) => (
                          <option key={val} value={val} />
                        ))}
                      </datalist>
                    </>
                  )}
                  <input
                    id={`edit-${variable.id}-description`}
                    type="text"
                    value={editDescriptions[variable.id] ?? ''}
                    onChange={(e) => setEditDescriptions({ ...editDescriptions, [variable.id]: e.target.value })}
                    placeholder="Optional description (e.g., notes about this value)"
                    className="description-input"
                  />
                </div>
              ))}

              <div className="form-actions">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="btn btn-secondary"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataPointManager;
